import {
    BookingCalendarConfig,
    BookingDeviceModalData,
    DateRangeWithOffset,
    DevicesLocationSelect,
    DevicesResponse,
    DevicesSearch,
    DevicesTypeSelect,
    SelectedBookingId,
    SelectedDeviceId,
} from '../types';
import BookingCalendarView from './BookingCalendarView';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearBookingsState,
    getBookingDevicesLocationsRequest,
    getBookingDevicesRequest,
    getBookingsRequest,
    getBookingsReset,
} from '@pages/booking/store';
import { Box, BoxProps } from '@mui/material';
import BookingCalendarToolbar from './BookingCalendarToolbar';
import BookingCalendarDevicesList from './BookingCalendarDevicesList';
import { getFavoritesRequest, getFavoritesReset } from '@features/favorites/store';
import { FavoriteObjectEntity } from '@features/favorites/types';
import { selectCurrentUser } from '@features/auth/store/selectors';
import BookingCalendarErrorHandlers from './BookingCalendarErrorHandlers';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { findWeekRangeForBooking, getWeekRangesAroundToday } from '@pages/booking/utils';
import { BOOKING_UPDATE_INTERVAL_S } from '@pages/booking/constants';
import { useBookingChunk } from '@pages/booking/hooks/useBookingChunk';
import { selectBookingDevices, selectGetBookingDevicesStatus } from '@pages/booking/store/selectors';
import { RequestStatusEnum } from '@shared/types';
import { BookingConfigContext } from '@pages/booking/contexts/BookingConfigContext';
import { BookingRangeContext } from '@pages/booking/contexts/BookingRangeContext';
import { useSearchParams } from 'react-router-dom';
import { getBooking } from '@pages/booking/api';

interface BookingCalendarProps {
    isFavoritesTab?: boolean;
    isMyBookingsTab?: boolean;
    devicesExternal?: DevicesResponse;
    getDevicesStatusExternal?: RequestStatusEnum;
    toolbarProps?: BoxProps;
    config?: BookingCalendarConfig;
}

const BookingCalendar = (props: BookingCalendarProps) => (
    <ComponentWithChunks
        featuresArray={['booking']}
        hooksArray={[useBookingChunk]}
        Component={BookingCalendarComponent}
        {...props}
    />
);

const BookingCalendarComponent: FC<BookingCalendarProps> = ({
    isFavoritesTab,
    isMyBookingsTab,
    devicesExternal,
    getDevicesStatusExternal,
    toolbarProps,
    config = {},
}) => {
    const { hideDevicesColumn, localBookingsStorageMode, selectMode } = config;
    const weekRanges = useMemo(() => getWeekRangesAroundToday(), []);
    const [range, setRange] = useState<DateRangeWithOffset>(weekRanges.rangesByOffset[0]);
    const [devicesSearch, setDevicesSearch] = useState<DevicesSearch>('');
    const [devicesLocation, setDevicesLocation] = useState<DevicesLocationSelect>('');
    const [devicesType, setDevicesType] = useState<DevicesTypeSelect>('');
    const devices = useSelector(selectBookingDevices);
    const getDevicesStatus = useSelector(selectGetBookingDevicesStatus);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedBookingId: SelectedBookingId = Number(searchParams.get('booking_id')) || null;
    const [selectedDeviceId, setSelectedDeviceId] = useState<SelectedDeviceId>(
        Number(searchParams.get('device_id')) || null,
    );
    const [modalData, setModalData] = useState<BookingDeviceModalData | null>(null);
    const currentUser = useSelector(selectCurrentUser);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dispatch = useDispatch();
    const devicesLocal = useMemo(() => {
        return devicesExternal || devices;
    }, [devices, devicesExternal]);
    const getDevicesStatusLocal = getDevicesStatusExternal || getDevicesStatus;

    const mergeSearchParams = useCallback(
        (newParams: Record<string, number | null>) => {
            const params = new URLSearchParams(searchParams);

            Object.entries(newParams).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    params.delete(key);
                } else {
                    params.set(key, String(value));
                }
            });

            setSearchParams(params);
        },
        [searchParams, setSearchParams],
    );

    const setSelectedDeviceIdToUrl = useCallback(
        (newSelectedDeviceId: SelectedDeviceId) => {
            setSelectedDeviceId(newSelectedDeviceId);

            if (localBookingsStorageMode || selectMode) {
                // disable URL params in local mode
                return;
            }

            if (newSelectedDeviceId) {
                mergeSearchParams({ device_id: newSelectedDeviceId });
            } else {
                dispatch(clearBookingsState());
                mergeSearchParams({ device_id: null });
            }
        },
        [dispatch, localBookingsStorageMode, selectMode, mergeSearchParams],
    );

    const setSelectedBookingIdToUrl = useCallback(
        (newSelectedBookingId: SelectedBookingId) => {
            // disable URL params in local mode
            if (localBookingsStorageMode || selectMode) {
                return;
            }

            mergeSearchParams({ booking_id: newSelectedBookingId });
        },
        [localBookingsStorageMode, mergeSearchParams, selectMode],
    );

    useEffect(() => {
        if (!selectedBookingId || localBookingsStorageMode || modalData) {
            return;
        }

        const fetchBookingFromURLParams = async () => {
            try {
                const { status, data: booking } = await getBooking({ id: selectedBookingId });

                if (status === 200 && booking.pk && booking.device === selectedDeviceId) {
                    const weekRangeForBooking = findWeekRangeForBooking(booking, weekRanges);

                    if (weekRangeForBooking) {
                        setRange(weekRangeForBooking);
                        return;
                    }
                }

                // if failed to find booking or date range doesn't exist, remove booking_id from URL params
                setSelectedBookingIdToUrl(null);
            } catch (e) {
                // if failed to fetch booking, remove too
                setSelectedBookingIdToUrl(null);
            }
        };

        (() => fetchBookingFromURLParams())();
    }, [
        localBookingsStorageMode,
        modalData,
        selectedBookingId,
        selectedDeviceId,
        setSelectedBookingIdToUrl,
        weekRanges,
    ]);

    useEffect(() => {
        if (localBookingsStorageMode) {
            return;
        }

        dispatch(getFavoritesRequest({ entity: FavoriteObjectEntity.BOOKING_DEVICES }));

        return () => {
            dispatch(getFavoritesReset({ entity: FavoriteObjectEntity.BOOKING_DEVICES }));
        };
    }, [dispatch, localBookingsStorageMode]);

    useEffect(() => {
        if (localBookingsStorageMode) {
            return;
        }

        dispatch(getBookingDevicesLocationsRequest());
    }, [dispatch, localBookingsStorageMode]);

    useEffect(() => {
        if (localBookingsStorageMode) {
            return;
        }

        dispatch(clearBookingsState());
        dispatch(
            getBookingDevicesRequest({
                location: devicesLocation,
                type: devicesType,
                search: devicesSearch,
                favoriteUserId: isFavoritesTab ? currentUser : undefined,
                showOnlyMy: Boolean(isMyBookingsTab),
            }),
        );
    }, [
        devicesLocation,
        devicesType,
        devicesSearch,
        isFavoritesTab,
        isMyBookingsTab,
        currentUser,
        dispatch,
        localBookingsStorageMode,
    ]);

    // fetch bookings when params change
    useEffect(() => {
        if (!devicesLocal || !Object.values(devicesLocal).flat().length) {
            return;
        }

        intervalRef.current && clearInterval(intervalRef.current);

        if (selectedDeviceId) {
            dispatch(getBookingsRequest({ device: selectedDeviceId, range, requestMode: 'standard' }));

            if (!localBookingsStorageMode) {
                intervalRef.current = setInterval(() => {
                    dispatch(getBookingsRequest({ device: selectedDeviceId, range, requestMode: 'background-fetch' }));
                }, BOOKING_UPDATE_INTERVAL_S * 1000);
            }
        } else {
            dispatch(getBookingsReset());
        }

        return () => {
            intervalRef.current && clearInterval(intervalRef.current);
            dispatch(getBookingsReset());
        };
    }, [dispatch, selectedDeviceId, range, devicesLocal, localBookingsStorageMode]);

    useEffect(() => {
        // Automatically select device if is not selected

        if (devicesLocal) {
            if (
                Object.values(devicesLocal)
                    .flat()
                    .find((device) => device.pk === selectedDeviceId)
            ) {
                return;
            }

            const firstNotEmptyDeviceArray = Object.values(devicesLocal).filter((array) => array.length)[0];
            setSelectedDeviceIdToUrl(firstNotEmptyDeviceArray ? firstNotEmptyDeviceArray[0].pk : null);
        }
    }, [devicesLocal, selectedDeviceId, setSelectedDeviceIdToUrl]);

    return (
        <>
            <BookingConfigContext.Provider value={config}>
                <BookingRangeContext.Provider value={{ range, setRange, weekRanges }}>
                    <BookingCalendarToolbar
                        devicesSearch={devicesSearch}
                        setDevicesSearch={setDevicesSearch}
                        devicesLocation={devicesLocation}
                        setDevicesLocation={setDevicesLocation}
                        devicesType={devicesType}
                        setDevicesType={setDevicesType}
                        toolbarProps={toolbarProps}
                    />
                    <Box sx={{ display: 'flex', height: 'calc(100% - 36px)', minHeight: 0 }}>
                        {!hideDevicesColumn && (
                            <BookingCalendarDevicesList
                                devices={devicesLocal}
                                getDevicesStatus={getDevicesStatusLocal}
                                devicesType={devicesType}
                                selectedDeviceId={selectedDeviceId}
                                setSelectedDeviceId={setSelectedDeviceIdToUrl}
                            />
                        )}
                        <BookingCalendarView
                            devices={devicesLocal}
                            selectedDeviceId={selectedDeviceId}
                            selectedBookingId={selectedBookingId}
                            setSelectedBookingId={setSelectedBookingIdToUrl}
                            modalData={modalData}
                            setModalData={setModalData}
                        />
                    </Box>
                </BookingRangeContext.Provider>
            </BookingConfigContext.Provider>
            <BookingCalendarErrorHandlers />
        </>
    );
};

export default BookingCalendar;
